// FPV stick-to-rate curves. Pure math — unit-tested headless.
//
// Two modes, mirroring how real quads are tuned:
//  - "simple": one max rate per axis, linear stick → rate (the sim's original feel)
//  - "actual": Betaflight Actual Rates, the community-standard parameterisation.
//
// Actual Rates (from Betaflight's applyActualRates, rc_curves.c):
//   expof     = |x| * (x^5 * expo + x * (1 - expo))
//   angleRate = x * centerSensitivity + max(0, maxRate - centerSensitivity) * expof
//
// Its selling point: the slope at stick-centre is EXACTLY centerSensitivity
// (deg/s per full stick) no matter what max rate or expo you run, so you can
// tune "how twitchy is it around centre" and "how fast is a full flick"
// independently. Expo only shapes the curve between the two.

export type AxisRates = { center: number; max: number; expo: number };
export type RatesSimple = { roll: number; pitch: number; yaw: number };
export type RatesActual = { roll: AxisRates; pitch: AxisRates; yaw: AxisRates };

export type FlightAxis = "roll" | "pitch" | "yaw";
export const FLIGHT_AXES: FlightAxis[] = ["roll", "pitch", "yaw"];

// Free's tuned defaults (2026-07-24): softer than Betaflight stock — centre 70
// with a 420 max and near-zero expo on roll/pitch, calmer yaw at 60/240/0.
export const DEFAULT_ACTUAL: RatesActual = {
  roll: { center: 70, max: 420, expo: 0.11 },
  pitch: { center: 70, max: 420, expo: 0.11 },
  yaw: { center: 60, max: 240, expo: 0 },
};
export const DEFAULT_SIMPLE: RatesSimple = { roll: 600, pitch: 600, yaw: 600 };

/** Betaflight Actual Rates: stick x in [-1,1] → angular rate in deg/s. */
export function actualRate(x: number, r: AxisRates): number {
  const ax = Math.abs(x);
  const expof = ax * (x * x * x * x * x * r.expo + x * (1 - r.expo));
  const stickMovement = Math.max(0, r.max - r.center);
  return x * r.center + stickMovement * expof;
}

/** Simple linear rates: stick x in [-1,1] × max deg/s. */
export function simpleRate(x: number, maxDegS: number): number {
  return x * maxDegS;
}
